import React from 'react';
import { AlertTriangle, ArrowLeft, Lock } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { FEATURE_LABELS, getFeatureAccess, getPlanLabel, getStatusLabel, type SubscriptionFeature } from '../lib/subscription';
import { useAuthStore } from '../store/authStore';
import { InfoStateCard } from './ui/InfoStateCard';
import { PageLoader } from './ui/PageLoader';

type ProtectedFeatureProps = {
  allowedRoles?: string[];
  feature: SubscriptionFeature;
  children: React.ReactNode;
};

const getRedirectPath = (role: string | null | undefined) => {
  if (role === 'superadmin') return '/superadmin';
  if (role === 'admin_taller' || role === 'tecnico') return '/admin';
  if (role === 'cliente') return '/client';
  return '/';
};

export function ProtectedFeature({ allowedRoles, feature, children }: ProtectedFeatureProps) {
  const { initialized, user } = useAuthStore();

  if (!initialized) {
    return (
      <PageLoader
        title="Validando acceso"
        description="Estamos comprobando tu plan y el estado comercial del taller antes de abrir este módulo."
      />
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getRedirectPath(user.role)} replace />;
  }

  if (user.role !== 'admin_taller' && user.role !== 'tecnico') {
    return <>{children}</>;
  }

  const access = getFeatureAccess(user.taller, feature);
  if (access.allowed) {
    return <>{children}</>;
  }

  const isBillingRestriction = access.reason === 'billing';
  const dashboardPath = '/admin';
  const actionPath = isBillingRestriction ? '/admin/subscription' : '/admin/upgrade';
  const actionLabel = isBillingRestriction ? 'Revisar suscripción' : 'Ver planes';

  return (
    <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center">
      <InfoStateCard
        eyebrow={isBillingRestriction ? getStatusLabel(user.taller?.status) : getPlanLabel(user.taller)}
        title={access.title || `${FEATURE_LABELS[feature]} bloqueado`}
        description={`${access.description || ''} Plan actual: ${getPlanLabel(user.taller)}. Estado: ${getStatusLabel(user.taller?.status)}.`}
        icon={isBillingRestriction ? <AlertTriangle className="h-7 w-7" /> : <Lock className="h-7 w-7" />}
        action={
          <>
            <Link
              to={dashboardPath}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver al dashboard
            </Link>
            {user.role === 'admin_taller' && (
              <Link
                to={actionPath}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-emerald-700"
              >
                {actionLabel}
              </Link>
            )}
          </>
        }
      />
    </div>
  );
}
